import { AfterViewInit, Component, ElementRef, OnDestroy, effect, inject, input, signal } from '@angular/core';

@Component({
    selector: 'app-collapsed-names',
    imports: [],
    templateUrl: './collapsed-names.component.html',
    host: { class: 'caption fs-6 fw-bold text-uppercase d-block' },
})
export class CollapsedNamesComponent implements AfterViewInit, OnDestroy {
    readonly names = input<string[]>([]);

    readonly visibleNames = signal<string[]>([]);
    readonly hiddenCount = signal(0);

    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly canvasContext = document.createElement('canvas').getContext('2d');
    private resizeObserver?: ResizeObserver;

    constructor() {
        effect(() => {
            this.names();
            this.recalculate();
        });
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.recalculate());
        this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private recalculate(): void {
        const names = this.names();
        const width = this.elementRef.nativeElement.clientWidth;

        if (!names.length || !width || !this.canvasContext) {
            this.visibleNames.set(names);
            this.hiddenCount.set(0);
            return;
        }

        const style = getComputedStyle(this.elementRef.nativeElement);
        this.canvasContext.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

        const measure = (text: string) => this.canvasContext!.measureText(text.toUpperCase()).width;
        const separatorWidth = measure(', ');

        let usedWidth = 0;
        let visibleCount = 0;

        for (let index = 0; index < names.length; index++) {
            const remaining = names.length - index - 1;
            // 28px approximates the badge's own padding/border, not measured from CSS.
            const badgeWidth = remaining > 0 ? measure(`+ ${remaining}`) + 28 : 0;
            const nameWidth = measure(names[index]) + (index > 0 ? separatorWidth : 0);

            if (index > 0 && usedWidth + nameWidth + badgeWidth > width) {
                break;
            }

            usedWidth += nameWidth;
            visibleCount++;
        }

        this.visibleNames.set(names.slice(0, visibleCount));
        this.hiddenCount.set(names.length - visibleCount);
    }
}
